import {
  TRAVELLER_PLAN,
  TRAVELLER_AGE,
  TRAVELLER_BENEFIT,
  TRAVELLER_CLEAR_PLAN,
  TRAVELLER_CLEAR_ALL_PLANS,
  TRAVELLER_CLEAR_BENEFIT,
  TRAVELLERS_PLAN_PARAMS,
  TravellerAge,
  TravellerProductDetails,
  TravellerPlanPayload,
  ClearPlan,
  TravellerProduct,
  TravellerParams,
  PRODUCT_TYPE,
  TRAVELLER_ELIGIBILITY,
  TRAVELLER_EXCLUSIONS,
  TRAVELLER_AMF_REQUIREMENTS,
  TRAVELLER_PLAN_COVERAGE,
  TRAVELLER_SYNC_RATES,
  TravellersPlanReducerPayload,
  CLEAR_TRAVELLERS_PLAN_STATE,
  CLEAR_TRAVELLERS_PLAN_STATE_PAX
} from './types';
import { evaluateAge } from './actions';
import { filter, find, map, reduce, get, concat, isEmpty, includes } from 'lodash';
import { RESET_PAX, RESET_STORE, STORE, WhitelistAction } from '../global';
import { checkWhitelist } from '../reducer-utils';

/**
 * Helper to get the plan code for a product.
 * For plans, returns the product code directly.
 * For optional benefits, finds the corresponding plan code.
 */
const getPlanCodeForProduct = (
  product: TravellerProduct,
  allProducts: TravellerProduct[]
): string | undefined => {
  if (product.type === PRODUCT_TYPE.PLAN) {
    return product.code;
  }
  return find(allProducts, p => p.paxId === product.paxId && p.type === PRODUCT_TYPE.PLAN)?.code;
};

/**
 * Helper to find the fresh plan data from quote.
 */
type QuoteByPax = Record<
  string,
  { referenceNumber?: number; quoteData?: { paxes?: Array<{ plans?: Array<Record<string, unknown>> }> } }
>;

type FreshPlanData = {
  basePremium?: number;
  baseTaxTotal?: number;
  basePremiumTotal?: number;
  familyRate?: number;
  familyMemberBasePremium?: number;
  optionalBenefits?: Array<{ code?: string; premium?: number; taxTotal?: number; premiumTotal?: number }>;
};

const getFreshPlanData = (
  quote: QuoteByPax,
  paxId: number,
  planCode: string | undefined
) => {
  const plans = quote?.[String(paxId)]?.quoteData?.paxes?.[0]?.plans ?? [];
  return find(plans, p => p.code === planCode) as
    | FreshPlanData
    | undefined;
};

/**
 * Helper to process optional benefit product.
 */
const processOptionalBenefitProduct = (
  product: TravellerProduct,
  freshPlanData: FreshPlanData | undefined
): TravellerProduct => {
  if (!freshPlanData) {
    return product;
  }
  const freshBenefit = find(freshPlanData.optionalBenefits ?? [], benefit => benefit.code === product.code);
  if (!freshBenefit) {
    return product;
  }
  return {
    ...product,
    premium: freshBenefit.premium ?? product.premium,
    tax: freshBenefit.taxTotal ?? product.tax,
    total: freshBenefit.premiumTotal ?? product.total
  };
};

/**
 * Helper to process plan product.
 */
const processPlanProduct = (
  product: TravellerProduct,
  freshPlanData: FreshPlanData | undefined
): TravellerProduct => {
  if (!freshPlanData) {
    return product;
  }
  return {
    ...product,
    premium: freshPlanData.basePremium ?? product.premium,
    tax: freshPlanData.baseTaxTotal ?? product.tax,
    total: freshPlanData.basePremiumTotal ?? product.total,
    ...(freshPlanData.familyRate != null && { familyRate: freshPlanData.familyRate }),
    ...(freshPlanData.familyMemberBasePremium != null && { familyPremium: freshPlanData.familyMemberBasePremium })
  };
};

/**
 * Helper to process and update a single product with fresh quote data.
 */
const updateProductWithFreshRate = (
  product: TravellerProduct,
  allProducts: TravellerProduct[],
  quote: QuoteByPax
): TravellerProduct => {
  if (product.type !== PRODUCT_TYPE.PLAN && product.type !== PRODUCT_TYPE.OPTIONAL_BENEFIT) {
    return product;
  }
  const planCode = getPlanCodeForProduct(product, allProducts);
  const freshPlanData = getFreshPlanData(quote, product.paxId, planCode);
  if (product.type === PRODUCT_TYPE.OPTIONAL_BENEFIT) {
    return processOptionalBenefitProduct(product, freshPlanData);
  }
  return processPlanProduct(product, freshPlanData);
};

const initialState = {
  individualPayment: 'false',
  legalConsent: false
};

const cases: Record<
  string,
  (
    state: TravellerProductDetails,
    action: {
      type: string;
      payload?: unknown;
    }
  ) => TravellerProductDetails
> = {
  [TRAVELLER_AGE]: (state, action) => {
    const act = action.payload as TravellerAge;
    const { products } = state;
    const existing = find(products, { paxId: act.paxId });
    const restProducts = filter(products, prod => prod.paxId !== act.paxId);
    const paxProducts = [];
    if (existing && existing.age === act.age) {
      reduce(
        products,
        (res, prod) => {
          prod.paxId === act.paxId && res.push({ ...prod, age: act.age, dateOfBirth: act.dateOfBirth });
          return res;
        },
        paxProducts
      );
    } else {
      paxProducts.push({ age: act.age, dateOfBirth: act.dateOfBirth, paxId: act.paxId });
    }
    return { ...state, products: [...restProducts, ...paxProducts] };
  },
  [TRAVELLER_PLAN]: (state, action) => {
    const act = action.payload as TravellerPlanPayload;
    const { products } = state;
    const restProducts = filter(products, (prod: TravellerProduct) => act.paxId !== prod.paxId);

    const planAvailable = find(
      products,
      (prod: TravellerProduct) => prod.paxId === act.paxId && prod.code === act.code
    );
    if (planAvailable || act.total <= 0) {
      return state;
    } else {
      const pax = find(products, (prod: TravellerProduct) => prod.paxId === act.paxId);
      const paxDetails = pax
        ? pax
        : evaluateAge({
          age: get(act, 'age'),
          format: get(act, 'dateFormat'),
          paxId: act.paxId
        });
      return {
        ...state,
        products: [
          ...restProducts,
          {
            ...act,
            age: paxDetails.age,
            dateOfBirth: paxDetails.dateOfBirth,
            type: PRODUCT_TYPE.PLAN
          }
        ],
        exclusions: null,
        eligibility: null
      };
    }
  },
  [TRAVELLER_BENEFIT]: (state, action) => {
    let act = action.payload as TravellerPlanPayload & { plan: string };
    const { products } = state;
    const restProducts = filter(products, (prod: TravellerProduct) => act.paxId !== prod.paxId);
    const restPaxProducts = filter(
      products,
      (prod: TravellerProduct) => act.paxId === prod.paxId && prod.code !== act.code
    );
    const planAvailable = find(
      products,
      (prod: TravellerProduct) => prod.paxId === act.paxId && prod.code === act.plan && prod.premium
    );
    const benefitAvailable = find(
      products,
      (prod: TravellerProduct) => prod.paxId === act.paxId && prod.code === act.code
    );
    if (benefitAvailable) {
      act = { ...benefitAvailable, ...act };
    }
    return planAvailable
      ? {
        ...state,
        products: [
          ...restProducts,
          ...restPaxProducts,
          {
            ...act,
            type: PRODUCT_TYPE.OPTIONAL_BENEFIT
          }
        ]
      }
      : state;
  },
  [TRAVELLER_CLEAR_PLAN]: (state, action) => {
    const act = action.payload as ClearPlan;
    const { products } = state;
    const restProducts = filter(products, (prod: TravellerProduct) => act.paxId !== prod.paxId);
    return {
      ...state,
      products: restProducts
    };
  },
  [TRAVELLER_CLEAR_ALL_PLANS]: state => {
    return {
      ...state,
      products: []
    };
  },
  [TRAVELLER_CLEAR_BENEFIT]: (state, action) => {
    const act = action.payload as ClearPlan;
    const { products } = state;
    const restProducts = filter(
      products,
      (prod: TravellerProduct) => act.paxId !== prod.paxId || (act.paxId === prod.paxId && prod.code !== act.code)
    );
    return {
      ...state,
      products: restProducts
    };
  },
  [TRAVELLERS_PLAN_PARAMS]: (state, action) => {
    const act = action.payload as TravellerParams;
    const { tripParams } = state;
    const existing = find(tripParams, {
      paxId: act.paxId,
      plan: act.plan
    });
    const existingTripParams = filter(
      tripParams,
      param => param.paxId !== act.paxId || (param.plan !== act.plan && param.paxId === act.paxId)
    );

    return {
      ...state,
      tripParams: existing
        ? [...existingTripParams, { ...existing, params: { ...existing.params, ...act.params } }]
        : [...existingTripParams, act]
    };
  },
  [TRAVELLER_EXCLUSIONS]: (state, action) => {
    const act = action.payload as boolean;
    return {
      ...state,
      exclusions: act
    };
  },
  [TRAVELLER_ELIGIBILITY]: (state, action) => {
    const act = action.payload as boolean;
    return {
      ...state,
      eligibility: act
    };
  },
  [TRAVELLER_PLAN_COVERAGE]: (state, action) => {
    const coverage = action.payload as string;
    return {
      ...state,
      coverage
    };
  },
  [TRAVELLER_AMF_REQUIREMENTS]: (state, action) => {
    const act = action.payload as boolean;
    return {
      ...state,
      amfRequirement: act
    };
  },
  [TRAVELLER_SYNC_RATES]: (state, action) => {
    const quote = action.payload as QuoteByPax;
    const products = map(state.products, (prod: TravellerProduct) =>
      updateProductWithFreshRate(prod, state.products, quote)
    );
    return { ...state, products };
  },
  [RESET_STORE]: (state, action) => {
    const { whitelist } = action.payload as WhitelistAction;
    return checkWhitelist(whitelist, STORE.TRAVELLER_PLAN, state, initialState);
  },
  [RESET_PAX]: (state, action) => {
    const { paxId } = action.payload as { paxId: number };
    const { age, dateOfBirth } = find(state.products, { paxId });
    const existing = isEmpty(dateOfBirth) ? [] : [{ age, dateOfBirth, paxId }];
    return {
      products: concat(
        filter(state.products, prod => paxId !== prod.paxId),
        existing
      ),
      tripParams: filter(state.tripParams, prod => paxId !== prod.paxId)
    };
  },
  [CLEAR_TRAVELLERS_PLAN_STATE]: () => ({}),
  [CLEAR_TRAVELLERS_PLAN_STATE_PAX]: (state, action) => {
    const { paxIds } = action.payload as { paxIds: number[] };
    const filterByPaxId = e => !includes(paxIds, e.paxId);
    return {
      products: concat(filter(state.products, item => filterByPaxId(item))),
      tripParams: concat(filter(state.tripParams, item => filterByPaxId(item)))
    };
  }
};

export const travellersPlan = (
  state: TravellerProductDetails = {},
  action: {
    type: string;
    payload?: TravellersPlanReducerPayload;
  }
): TravellerProductDetails => {
  const reducer = cases[action.type];
  return reducer ? reducer(state, action) : state;
};
